// Recent working postcodes, offered as one-tap chips on the postcode screen
// and in the Change Location drawer (DEV-51044).
//
// Stored in localStorage, not sessionStorage: "recent" is only useful if it
// survives closing the tab, so an engineer who worked three sites yesterday
// sees them this morning. Keyed per tenant + user, because logout clears only
// the auth tokens — on a shared device one engineer must never be offered
// another's job postcodes.

import { normalizePostcode } from './postcodes'

const RECENT_LOCATIONS_STORAGE_PREFIX = 'recent_locations'

/** Chips shown to the engineer (AC6). */
export const MAX_RECENT_LOCATIONS = 3

/**
 * How many are kept, which is one more than is shown.
 *
 * The newest entry is always the postcode just applied — the one in use —
 * and AC6 also says the postcode in use doesn't appear. Keeping only three
 * would therefore leave two chips visible and never three, however many
 * locations the engineer had been to.
 */
const STORED_RECENT_LOCATIONS = MAX_RECENT_LOCATIONS + 1

// Holds the last value whose write failed (storage disabled, full, or blocked
// for privacy) so chips still work for this page life. A later successful
// write clears the override and storage rules again.
const memoryFallback = new Map<string, string | null>()

const listeners = new Set<() => void>()

/**
 * Storage key for a user, or null while the user id is unknown.
 *
 * Nothing is read or written against a null key, so one engineer's recents
 * can never land under another's on a shared device.
 */
export function buildRecentLocationsKey(
  tenantSlug: string | undefined,
  userId: string | undefined,
): string | null {
  if (!userId) return null
  return `${RECENT_LOCATIONS_STORAGE_PREFIX}_${tenantSlug ?? 'default'}_${userId}`
}

export function subscribeToRecentLocations(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * The raw stored string, so a `useSyncExternalStore` snapshot stays
 * referentially stable and parsing can happen in a memo.
 */
export function readRecentLocationsRaw(
  storageKey: string | null,
): string | null {
  if (storageKey === null || typeof window === 'undefined') return null
  if (memoryFallback.has(storageKey)) {
    return memoryFallback.get(storageKey) ?? null
  }
  try {
    return window.localStorage.getItem(storageKey)
  } catch {
    return null
  }
}

/** Parse a stored snapshot, newest first. Corrupt data reads as empty. */
export function parseRecentLocations(raw: string | null): string[] {
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((entry): entry is string => typeof entry === 'string')
      .slice(0, STORED_RECENT_LOCATIONS)
  } catch {
    // Corrupt storage — offer no chips rather than break the screen.
    return []
  }
}

/**
 * Record a postcode as the most recent one used on this device.
 *
 * Any earlier entry for the same postcode is dropped first, so the list holds
 * distinct postcodes. A storage failure is swallowed: chips are a convenience
 * and must never stop an engineer setting their location.
 */
export function rememberRecentLocation(
  storageKey: string | null,
  postcode: string,
): void {
  if (storageKey === null || typeof window === 'undefined') return
  const trimmedPostcode = postcode.trim()
  if (!trimmedPostcode) return

  const existing = parseRecentLocations(readRecentLocationsRaw(storageKey))
  const withoutDuplicate = existing.filter(
    (recent) => normalizePostcode(recent) !== normalizePostcode(trimmedPostcode),
  )
  const next = [trimmedPostcode, ...withoutDuplicate].slice(
    0,
    STORED_RECENT_LOCATIONS,
  )
  const serialised = JSON.stringify(next)

  try {
    window.localStorage.setItem(storageKey, serialised)
    memoryFallback.delete(storageKey)
  } catch {
    // Storage full / disabled — keep it in memory so the chips still update
    // for this page life.
    memoryFallback.set(storageKey, serialised)
  }

  for (const listener of listeners) listener()
}
